/**
 * Notifiche della chat nella pagina del sito (notifiche fase A, 20/10/2026, spec §10 e §17).
 *
 * La pagina NON crea notifiche: la web push parte sempre (spec C3) e una notifica locale sarebbe un
 * doppione. La pagina suona leggero quando, con la finestra in primo piano, arriva un messaggio
 * altrui; chiude le notifiche delle chat lette o aperte (tag `chat-<id>`, come il service worker)
 * e aggiorna il numero sull'icona dell'app installata.
 */

import type { ChatConversazione, ChatMessaggio } from '../../../shared/types/chat';

/** Al più un suono «ricevuto» ogni 2 s. */
export const INTERVALLO_SUONO_MS = 2000;
export const SUONO_RICEVUTO = '/suoni/chat-ricevuto.wav';
const VOLUME_SUONO = 0.5;

export interface CasoSuono {
  messaggio: ChatMessaggio;
  /** La conversazione in elenco (null se non c'è ancora). */
  conversazione: ChatConversazione | null;
  mioId: string | null;
  finestraAFuoco: boolean;
  ora: number;
  /** Ora dell'ultimo suono (0 = mai). */
  ultimoSuono: number;
}

/**
 * Suono leggero all'arrivo di un messaggio altrui con la finestra in primo piano, al più uno ogni
 * 2 s. Le chat silenziate restano mute, salvo una menzione che mi riguarda (come la push).
 */
export const deveSuonareRicevuto = ({ messaggio, conversazione, mioId, finestraAFuoco, ora, ultimoSuono }: CasoSuono): boolean => {
  if (!messaggio || messaggio.mio || messaggio.tipo === 'sistema') return false;
  if (!finestraAFuoco) return false;
  if (ultimoSuono && ora - ultimoSuono < INTERVALLO_SUONO_MS) return false;
  const fino = conversazione && conversazione.silenziataFino ? Date.parse(conversazione.silenziataFino) : NaN;
  if (!isNaN(fino) && fino > ora) {
    const menzioni = Array.isArray(messaggio.menzioni) ? messaggio.menzioni : [];
    return !!mioId && menzioni.some((m) => m && m.id === mioId);
  }
  return true;
};

/** Id da usare in un percorso (`/area-riservata/chat/<id>`): niente barre né punti. */
export const idConversazioneValido = (id: unknown): id is string =>
  typeof id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(id);

type ContenitoreSw = Pick<ServiceWorkerContainer, 'getRegistration'>;

const contenitoreSw = (): ContenitoreSw | undefined =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : undefined;

/** Chiude le notifiche della chat `conversazioneId` (tag `chat-<id>`). Mai lancia. */
export const chiudiNotificheChat = async (conversazioneId: string, sw: ContenitoreSw | undefined = contenitoreSw()): Promise<void> => {
  try {
    if (!sw || typeof sw.getRegistration !== 'function') return;
    const registrazione = await sw.getRegistration();
    if (!registrazione || typeof registrazione.getNotifications !== 'function') return;
    const notifiche = await registrazione.getNotifications({ tag: `chat-${conversazioneId}` });
    notifiche.forEach((n) => n.close());
  } catch {
    // Notifiche non raggiungibili: restano, le chiude la prossima push (riconciliazione).
  }
};

interface NavigatoreBadge {
  setAppBadge?: (n?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

/** Numero sull'icona dell'app installata (0 ⇒ via), se il browser lo permette. Mai lancia. */
export const aggiornaBadgeApp = async (
  totale: number,
  nav: NavigatoreBadge | undefined = typeof navigator !== 'undefined' ? (navigator as unknown as NavigatoreBadge) : undefined
): Promise<void> => {
  if (!nav || typeof totale !== 'number' || isNaN(totale)) return;
  try {
    if (totale > 0) {
      if (typeof nav.setAppBadge === 'function') await nav.setAppBadge(totale);
    } else if (typeof nav.clearAppBadge === 'function') {
      await nav.clearAppBadge();
    }
  } catch {
    // Badge non permesso (sito non installato, permesso negato): niente.
  }
};

/** Suono «ricevuto» leggero. Mai lancia (autoplay bloccato prima di un'interazione: silenzio). */
export const suonaRicevuto = (): void => {
  try {
    const Lettore = typeof window !== 'undefined' ? (window as any).Audio : undefined;
    if (typeof Lettore !== 'function') return;
    const audio: HTMLAudioElement = new Lettore(SUONO_RICEVUTO);
    audio.volume = VOLUME_SUONO;
    const esito = audio.play();
    if (esito && typeof esito.catch === 'function') esito.catch(() => undefined);
  } catch {
    // Nessun suono: non disturba la lettura.
  }
};
